// Safe HTML for content written by shops/users (e.g. mall "Rich Text" sections).
// Parses with DOMParser (an inert document: nothing in it runs or loads), keeps
// only simple formatting tags, drops every attribute except a safe link href,
// and returns the cleaned HTML. Anything else (script, iframe, img onerror,
// style, svg, forms, javascript: links ...) is removed.
const ALLOWED_TAGS = new Set([
  "p", "br", "b", "strong", "i", "em", "u", "s", "small", "mark", "span", "div",
  "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote", "hr", "a",
]);
const DROP_WITH_CONTENT = new Set(["script", "style", "iframe", "object", "embed", "template", "noscript", "svg", "math", "textarea", "select", "button", "form", "title", "head"]);

export function safeHref(href) {
  const h = String(href || "").trim();
  if (!h) return "";
  if (h.startsWith("/") && !h.startsWith("//")) return h; // same-site path
  if (h.startsWith("#")) return h;
  if (h.startsWith("?")) return h; // same-page query (e.g. ?showAll=true)
  // Bare domains ("instagram.com/x", "www.shop.com") → https://
  const withScheme = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#].*)?$/i.test(h) ? `https://${h}` : h;
  try {
    const u = new URL(withScheme);
    return ["http:", "https:", "mailto:", "tel:"].includes(u.protocol) ? u.href : "";
  } catch {
    return "";
  }
}

export function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export function sanitizeHtml(html) {
  const src = String(html || "");
  if (!src) return "";
  if (typeof DOMParser === "undefined") return escapeHtml(src);
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${src}`, "text/html");
  const clean = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) continue; // text
      if (child.nodeType !== 1) { child.remove(); continue; } // comments etc.
      const tag = child.tagName.toLowerCase();
      if (DROP_WITH_CONTENT.has(tag)) { child.remove(); continue; }
      if (!ALLOWED_TAGS.has(tag)) {
        clean(child);
        child.replaceWith(...Array.from(child.childNodes)); // keep the text, drop the tag
        continue;
      }
      const href = tag === "a" ? safeHref(child.getAttribute("href")) : "";
      for (const a of Array.from(child.attributes)) child.removeAttribute(a.name);
      if (tag === "a") {
        if (href) {
          child.setAttribute("href", href);
          if (!href.startsWith("/") && !href.startsWith("#")) {
            child.setAttribute("target", "_blank");
            child.setAttribute("rel", "noopener noreferrer nofollow");
          }
        }
      }
      clean(child);
    }
  };
  clean(doc.body);
  return doc.body.innerHTML;
}
